import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard,
    MapPin,
    ArrowLeftRight,
    Users,
    FileText,
    Pill,
    Stethoscope,
    Calendar,
    BedDouble,
    WarehouseIcon,
    Mail,
    Phone,
    ChevronLeft,
    ChevronRight,
    Leaf,
    Settings,
    Building2,
    UserPlus,
    ClipboardCheck,
    Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SPRING } from '@/lib/motion';
import { useAuth } from '@/hooks/useAuth';
import { useWoundApprovalAccess } from '@/hooks/useWoundApprovalAccess';
import { usePracticeRequestStatsQuery } from '@/hooks/usePracticeRequests';
import { useFacility } from '@/contexts/FacilityContext';
import { useCareFeatureGateContext } from '@/contexts/CareFeatureGateContext';
import { prefetchRoute } from '@/routes/prefetch';
import { getWoundStats } from '@/services/wound.service';
import type { CareFeatureKey } from '@/types/care-features';

interface NavItem {
    label: string;
    icon: React.ElementType;
    to: string;
    badge?: number;
    featureKey?: CareFeatureKey;
}

interface NavGroup {
    label?: string;
    items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
    {
        items: [
            { label: 'Station', icon: LayoutDashboard, to: '/dashboard' },
            { label: 'Lageübersicht', icon: MapPin, to: '/location-overview' },
            { label: 'Übergabe', icon: ArrowLeftRight, to: '/handover' },
        ],
    },
    {
        label: 'Bewohner',
        items: [
            { label: 'Bewohnerliste', icon: Users, to: '/residents' },
            { label: 'SIS-Dokumentation', icon: FileText, to: '/sis', featureKey: 'careSisDocumentation' },
            { label: 'Wundmanagement', icon: Stethoscope, to: '/wounds', featureKey: 'careWoundMgmt' },
            { label: 'Medikamente', icon: Pill, to: '/medications', featureKey: 'careMedications' },
        ],
    },
    {
        label: 'Verwaltung',
        items: [
            { label: 'Aufnahmen', icon: UserPlus, to: '/admissions', featureKey: 'careAdmissions' },
            { label: 'Belegung', icon: BedDouble, to: '/occupancy', featureKey: 'careOccupancy' },
            { label: 'Lager', icon: WarehouseIcon, to: '/inventory', featureKey: 'careInventory' },
            { label: 'Dienstplan', icon: Calendar, to: '/schedule', featureKey: 'careRoster' },
        ],
    },
    {
        label: 'Kommunikation',
        items: [
            { label: 'Postfach', icon: Mail, to: '/inbox', featureKey: 'careInbox' },
            { label: 'Kontakte', icon: Phone, to: '/contacts', featureKey: 'careContacts' },
            { label: 'Praxisanfragen', icon: Building2, to: '/practice-requests', featureKey: 'carePracticeRequests' },
            { label: 'Netzwerk', icon: Globe, to: '/netzwerk', featureKey: 'carePracticeRequests' },
        ],
    },
    {
        label: 'System',
        items: [
            { label: 'Einstellungen', icon: Settings, to: '/settings' },
        ],
    },
];

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

function NavItemComp({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
    const location = useLocation();
    const isActive = location.pathname.startsWith(item.to);
    const Icon = item.icon;
    const triggerRoutePrefetch = () => prefetchRoute(item.to);

    return (
        <NavLink
            to={item.to}
            title={collapsed ? item.label : undefined}
            onMouseEnter={triggerRoutePrefetch}
            onFocus={triggerRoutePrefetch}
        >
            {({ isActive: routerActive }) => (
                <div className="relative">
                    {(isActive || routerActive) && (
                        <motion.div
                            layoutId="care-sidebar-active"
                            className="absolute inset-0 bg-brand-500/10 dark:bg-brand-500/15 rounded-xl"
                            transition={SPRING.smooth}
                        />
                    )}
                    <div
                        className={cn(
                            'relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors',
                            (isActive || routerActive)
                                ? 'text-brand-600 dark:text-brand-400 font-medium'
                                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                        )}
                    >
                        <Icon className="h-5 w-5 shrink-0" />
                        <AnimatePresence initial={false}>
                            {!collapsed && (
                                <motion.span
                                    initial={{ opacity: 0, x: -6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -6 }}
                                    transition={SPRING.snappy}
                                    className="text-sm overflow-hidden whitespace-nowrap"
                                >
                                    {item.label}
                                </motion.span>
                            )}
                        </AnimatePresence>
                        {item.badge !== undefined && item.badge > 0 && (
                            <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className={cn(
                                    'ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white px-1',
                                    collapsed && 'absolute -top-1 -right-1 ml-0',
                                )}
                            >
                                {item.badge > 99 ? '99+' : item.badge}
                            </motion.span>
                        )}
                    </div>
                </div>
            )}
        </NavLink>
    );
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
    const { user } = useAuth();
    const { settings, facilityId } = useFacility();
    const { isFeatureEnabled, loading: featuresLoading } = useCareFeatureGateContext();
    const { data: practiceRequestStats } = usePracticeRequestStatsQuery(facilityId ?? undefined);
    const { data: approvalAccess } = useWoundApprovalAccess();
    const { data: woundStats } = useQuery({
        queryKey: ['wound-stats', facilityId],
        queryFn: () => getWoundStats(facilityId!),
        enabled: !!facilityId,
        staleTime: 60_000,
    });

    const navGroups: NavGroup[] = NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items
            .filter((item) => {
                // Kein featureKey → immer sichtbar (Kern-Feature)
                if (!item.featureKey) return true;
                // Während Laden alle Items zeigen
                if (featuresLoading) return true;
                return isFeatureEnabled(item.featureKey);
            })
            .map((item) =>
                item.to === '/practice-requests'
                    ? { ...item, badge: practiceRequestStats?.total ?? 0 }
                    : item.to === '/wounds'
                        ? { ...item, badge: approvalAccess?.canApprove ? (woundStats?.pendingApproval ?? 0) : undefined }
                        : item,
            ),
    })).map((group) => {
        if (group.label !== 'Bewohner' || !approvalAccess?.canApprove || !isFeatureEnabled('careWoundMgmt')) {
            return group;
        }

        const items = group.items.flatMap((item) => (
            item.to === '/wounds'
                ? [
                    item,
                    {
                        label: 'Wundfreigaben',
                        icon: ClipboardCheck,
                        to: '/wounds/approval',
                        badge: woundStats?.pendingApproval ?? 0,
                        featureKey: 'careWoundMgmt' as CareFeatureKey,
                    },
                ]
                : [item]
        ));

        return { ...group, items };
    });

    return (
        <aside
            className="relative flex h-full flex-col border-r shrink-0 overflow-hidden"
            style={{
                backgroundColor: 'var(--surface-sidebar)',
                borderColor: 'var(--border-default)',
                // CSS transition replaces framer-motion JS animation.
                // overflow-hidden clips text labels during collapse (no need for width:0 on children).
                width: collapsed ? 72 : 256,
                transition: "width 300ms cubic-bezier(0.32, 0.72, 0, 1)",
            }}
        >
            {/* Logo / Brand */}
            <div className={cn('flex items-center gap-3 p-4 h-16 border-b')} style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white">
                    <Leaf className="h-5 w-5" />
                </div>
                <AnimatePresence initial={false}>
                    {!collapsed && (
                        <motion.div
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -8 }}
                            transition={SPRING.snappy}
                            className="overflow-hidden"
                        >
                            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 leading-none whitespace-nowrap">
                                MediFlow
                            </p>
                            <p className="text-xs text-brand-500 font-medium whitespace-nowrap">Pflege</p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Nav Groups */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-6">
                {navGroups.map((group, gi) => (
                    <div key={gi}>
                        <AnimatePresence initial={false}>
                            {!collapsed && group.label && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-600"
                                >
                                    {group.label}
                                </motion.p>
                            )}
                        </AnimatePresence>
                        <div className="space-y-0.5">
                            {group.items.map((item) => (
                                <NavItemComp key={item.to} item={item} collapsed={collapsed} />
                            ))}
                        </div>
                    </div>
                ))}
            </nav>

            {/* User Footer */}
            <div className={cn('border-t border-neutral-100 dark:border-neutral-800 p-3')}>
                <AnimatePresence initial={false}>
                    {!collapsed ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center gap-2.5 px-2 py-1.5"
                        >
                            <div className="h-8 w-8 shrink-0 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-600 dark:text-brand-400 text-xs font-bold">
                                {user?.displayName?.[0]?.toUpperCase() ?? user?.email[0]?.toUpperCase() ?? '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                    {user?.displayName || user?.email || '–'}
                                </p>
                                <p className="text-[10px] text-neutral-400 dark:text-neutral-500 truncate">
                                    {settings.facilityName}
                                </p>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex justify-center"
                        >
                            <div className="h-8 w-8 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-brand-600 dark:text-brand-400 text-xs font-bold">
                                {user?.displayName?.[0]?.toUpperCase() ?? user?.email[0]?.toUpperCase() ?? '?'}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Toggle Button */}
            <button
                onClick={onToggle}
                className="absolute -right-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-500 hover:text-brand-600 dark:hover:text-brand-400 shadow-sm transition-colors z-10"
                title={collapsed ? 'Sidebar aufklappen' : 'Sidebar einklappen'}
            >
                {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
            </button>
        </aside>
    );
}
